import React from "react";
import { Button, Modal } from "@hid-galaxy-ui/galaxy-react";
import RemoveScreenshotModal from "./RemoveScreenshotModal";
import ScreenshotRemoveIcon from "./ScreenshotRemoveIcon";
import ScreenshotMedia from "./ScreenshotMedia";
import {
  DEFAULT_SCREENSHOT_MAX_FILES,
  formatScreenshotSize,
  SCREENSHOT_ACCEPTED_TYPES,
  SCREENSHOT_MAX_SIZE_MB,
  SCREENSHOT_MIN_FILES,
  SCREENSHOT_TEMPLATE_SLOTS,
  SCREENSHOT_TEMPLATES,
  ScreenshotItem,
  ScreenshotTemplate,
} from "./screenshotsConfig";
import "./ScreenshotModals.scss";

export type AddScreenshotsSavePayload = {
  template: ScreenshotTemplate;
  selectedMediaIds: number[];
  selectedIndexes: number[];
  files: File[];
};

type AddScreenshotsModalProps = {
  initialTemplate: ScreenshotTemplate | null;
  initialScreenshots: ScreenshotItem[];
  initialSelectedIds: string[];
  maxFiles?: number;
  isSaving?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: (payload: AddScreenshotsSavePayload) => void;
  onDeleteScreenshot: (
    item: ScreenshotItem,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => void;
};

const SelectedIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="11" fill="currentColor" />
    <path
      d="m6.5 11.2 3 3 6-6.2"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const TemplateGlyph: React.FC<{ template: ScreenshotTemplate }> = ({ template }) => {
  const shapes =
    template === "mobile_desktop"
      ? ["mobile", "desktop"]
      : template === "two_desktops"
        ? ["desktop", "desktop"]
        : ["mobile", "mobile", "mobile"];

  return (
    <span
      className={`screenshots-modal__template-glyph screenshots-modal__template-glyph--${template}`}
      aria-hidden="true"
    >
      {shapes.map((shape, index) => (
        <span
          key={`${shape}-${index}`}
          className={`screenshots-modal__template-shape screenshots-modal__template-shape--${shape}`}
        />
      ))}
    </span>
  );
};

const UploadCloudIcon: React.FC = () => (
  <svg width="46" height="40" viewBox="0 0 46 40" fill="none" aria-hidden="true">
    <path
      d="M15 30h-3.5a9.5 9.5 0 0 1-1.6-18.87A12 12 0 0 1 33.3 9.4 8.3 8.3 0 0 1 35 30h-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M23 37V20m0 0-6 6m6-6 6 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7.2V11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5" r="0.8" fill="currentColor" />
  </svg>
);

const revokeLocalUrl = (item: ScreenshotItem) => {
  if (item.file && item.url?.startsWith("blob:")) {
    URL.revokeObjectURL(item.url);
  }
};

const AddScreenshotsModal: React.FC<AddScreenshotsModalProps> = ({
  initialTemplate,
  initialScreenshots,
  initialSelectedIds,
  maxFiles = DEFAULT_SCREENSHOT_MAX_FILES,
  isSaving = false,
  errorMessage: saveErrorMessage = null,
  onClose,
  onSave,
  onDeleteScreenshot,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [template, setTemplate] = React.useState<ScreenshotTemplate | null>(
    initialTemplate
  );
  const [screenshots, setScreenshots] = React.useState(initialScreenshots);
  const [selectedIds, setSelectedIds] = React.useState<string[]>(() => {
    const slots = initialTemplate
      ? SCREENSHOT_TEMPLATE_SLOTS[initialTemplate]
      : initialSelectedIds.length;
    return initialSelectedIds.slice(0, slots);
  });
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] =
    React.useState<ScreenshotItem | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const screenshotsRef = React.useRef(screenshots);
  screenshotsRef.current = screenshots;
  React.useEffect(
    () => () => {
      screenshotsRef.current.forEach(revokeLocalUrl);
    },
    []
  );

  const requiredSelections = template ? SCREENSHOT_TEMPLATE_SLOTS[template] : 0;
  const hasNewFiles = screenshots.some((item) => item.file);
  const isAtMaxFiles = screenshots.length >= maxFiles;

  const isDirty =
    template !== initialTemplate ||
    hasNewFiles ||
    !sameIds(selectedIds, initialSelectedIds);

  const validationMessage = (() => {
    if (!template) return "Select a template to continue.";
    if (screenshots.length < SCREENSHOT_MIN_FILES) {
      return `Upload at least ${SCREENSHOT_MIN_FILES} screenshot.`;
    }
    if (screenshots.length > maxFiles) {
      return `You can upload up to ${maxFiles} screenshots.`;
    }
    if (selectedIds.length !== requiredSelections) {
      return `Select ${requiredSelections} screenshot${
        requiredSelections === 1 ? "" : "s"
      } for this template.`;
    }
    return null;
  })();

  const canSave = !validationMessage && isDirty && !isSaving;

  const handleTemplateChange = (nextTemplate: ScreenshotTemplate) => {
    setTemplate(nextTemplate);
    setSelectedIds((prev) =>
      prev.slice(0, SCREENSHOT_TEMPLATE_SLOTS[nextTemplate])
    );
  };

  const toggleSelection = (item: ScreenshotItem) => {
    if (isSaving) return;
    setSelectedIds((prev) => {
      if (prev.includes(item.id)) return prev.filter((id) => id !== item.id);
      if (!template || prev.length >= requiredSelections) return prev;
      return [...prev, item.id];
    });
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || isSaving) return;

    const errors: string[] = [];
    const accepted: ScreenshotItem[] = [];
    const remainingSlots = maxFiles - screenshots.length;

    Array.from(fileList).forEach((file) => {
      if (!SCREENSHOT_ACCEPTED_TYPES.includes(file.type)) {
        errors.push(`${file.name} is not a PNG or JPG file.`);
        return;
      }
      if (file.size > SCREENSHOT_MAX_SIZE_MB * 1024 * 1024) {
        errors.push(`${file.name} exceeds the ${SCREENSHOT_MAX_SIZE_MB}MB limit.`);
        return;
      }
      if (accepted.length >= remainingSlots) {
        errors.push(`You can upload up to ${maxFiles} screenshots.`);
        return;
      }
      accepted.push({
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        sizeBytes: file.size,
        url: URL.createObjectURL(file),
        mimeType: file.type,
        file,
      });
    });

    setErrorMessage(errors.length ? Array.from(new Set(errors)).join(" ") : null);
    if (!accepted.length) return;

    setScreenshots((prev) => [...prev, ...accepted]);
    if (template) {
      setSelectedIds((prev) => {
        const openSlots = Math.max(requiredSelections - prev.length, 0);
        return [...prev, ...accepted.slice(0, openSlots).map((item) => item.id)];
      });
    }
  };

  const handleClose = () => {
    if (isSaving || isRemoving) return;
    onClose();
  };

  const handleSave = () => {
    if (!canSave || !template) return;

    const newItems = screenshots.filter((item) => item.file);
    onSave({
      template,
      files: newItems.map((item) => item.file as File),
      selectedIndexes: newItems.reduce<number[]>((indexes, item, index) => {
        if (selectedIds.includes(item.id)) indexes.push(index);
        return indexes;
      }, []),
      selectedMediaIds: screenshots
        .filter((item) => !item.file && selectedIds.includes(item.id))
        .map((item) => Number(item.id)),
    });
  };

  const removeFromDraft = (target: ScreenshotItem) => {
    revokeLocalUrl(target);
    setScreenshots((prev) => prev.filter((item) => item.id !== target.id));
    setSelectedIds((prev) => prev.filter((id) => id !== target.id));
    setPendingRemoval(null);
  };

  const openRemoval = (item: ScreenshotItem) => {
    setRemoveError(null);
    setPendingRemoval(item);
  };

  const cancelRemoval = () => {
    if (isRemoving) return;
    setRemoveError(null);
    setPendingRemoval(null);
  };

  const confirmRemoval = () => {
    if (!pendingRemoval || isRemoving) return;
    if (pendingRemoval.file) {
      removeFromDraft(pendingRemoval);
      return;
    }

    const target = pendingRemoval;
    setIsRemoving(true);
    setRemoveError(null);
    onDeleteScreenshot(
      target,
      () => {
        setIsRemoving(false);
        removeFromDraft(target);
      },
      (message) => {
        setIsRemoving(false);
        setRemoveError(message);
      }
    );
  };

  const shownError = saveErrorMessage || errorMessage;

  return (
    <>
      <Modal
        headerText="Add Screenshots"
        width={780}
        onClose={handleClose}
        className="screenshots-modal"
      >
        <div className="screenshots-modal__content">
          <section className="screenshots-modal__group">
            <h4 className="screenshots-modal__label">Select Template</h4>
            <div className="screenshots-modal__templates" role="radiogroup">
              {SCREENSHOT_TEMPLATES.map((option) => {
                const isSelected = option.value === template;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={`screenshots-modal__template${
                      isSelected ? " screenshots-modal__template--selected" : ""
                    }`}
                    onClick={() => handleTemplateChange(option.value)}
                    disabled={isSaving}
                  >
                    <TemplateGlyph template={option.value} />
                    <span className="screenshots-modal__template-label">
                      {option.label}
                    </span>
                    <span className="screenshots-modal__template-hint">
                      {option.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <div
            className={`screenshots-modal__dropzone${
              isDragActive ? " screenshots-modal__dropzone--active" : ""
            }${isAtMaxFiles ? " screenshots-modal__dropzone--disabled" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isAtMaxFiles) setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragActive(false);
              if (isAtMaxFiles) {
                setErrorMessage(`You can upload up to ${maxFiles} screenshots.`);
                return;
              }
              addFiles(e.dataTransfer.files);
            }}
          >
            <span className="screenshots-modal__dropzone-icon">
              <UploadCloudIcon />
            </span>
            <p className="screenshots-modal__dropzone-title">
              Select a file or drag and drop here
            </p>
            <p className="screenshots-modal__dropzone-hint">
              JPG or PNG, file size no more than {SCREENSHOT_MAX_SIZE_MB}MB
            </p>
            <button
              type="button"
              className="screenshots-modal__select-file"
              onClick={() => fileInputRef.current?.click()}
              disabled={isAtMaxFiles || isSaving}
            >
              Select File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={SCREENSHOT_ACCEPTED_TYPES.join(",")}
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>

          {shownError ? (
            <p className="screenshots-modal__error">{shownError}</p>
          ) : null}

          {screenshots.length > 0 ? (
            <section className="screenshots-modal__group screenshots-modal__group--uploads">
              <div className="screenshots-modal__uploads-header">
                <h4 className="screenshots-modal__label">
                  Uploaded ({screenshots.length}/{maxFiles})
                </h4>
                {template ? (
                  <span
                    className={`screenshots-modal__selected-count${
                      selectedIds.length === requiredSelections
                        ? " screenshots-modal__selected-count--complete"
                        : ""
                    }`}
                  >
                    Selected {selectedIds.length}/{requiredSelections}
                  </span>
                ) : null}
              </div>
              <ul className="screenshots-modal__uploads">
                {screenshots.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  const isSelectable =
                    !!template &&
                    (isSelected || selectedIds.length < requiredSelections);
                  return (
                    <li key={item.id} className="screenshots-modal__upload">
                      <div
                        className={`screenshots-modal__thumb${
                          isSelected ? " screenshots-modal__thumb--selected" : ""
                        }${
                          !isSelectable ? " screenshots-modal__thumb--locked" : ""
                        }`}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`Select ${item.name}`}
                        tabIndex={0}
                        onClick={() => toggleSelection(item)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleSelection(item);
                          }
                        }}
                      >
                        <ScreenshotMedia
                          url={item.url}
                          name={item.name}
                          mimeType={item.mimeType}
                        />
                        {isSelected ? (
                          <span className="screenshots-modal__selected-badge">
                            <SelectedIcon />
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="screenshots-modal__remove"
                          aria-label={`Remove ${item.name}`}
                          disabled={isSaving || isRemoving}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRemoval(item);
                          }}
                        >
                          <ScreenshotRemoveIcon />
                        </button>
                      </div>
                      <span className="screenshots-modal__file-name" title={item.name}>
                        {item.name}
                      </span>
                      <span className="screenshots-modal__file-size">
                        {formatScreenshotSize(item.sizeBytes)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <div className="screenshots-modal__info">
            <InfoIcon />
            <span>
              Supported formats: PNG, JPG. Max size: {SCREENSHOT_MAX_SIZE_MB}MB per
              file. You can upload up to {maxFiles} screenshots.
            </span>
          </div>
        </div>

        <div className="screenshots-modal__actions">
          {validationMessage && isDirty ? (
            <span className="screenshots-modal__hint">{validationMessage}</span>
          ) : null}
          <button
            type="button"
            className="screenshots-modal__btn screenshots-modal__btn--secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <Button
            variant="primary"
            label={isSaving ? "Uploading..." : "Upload Screenshots"}
            className="screenshots-modal__upload-btn"
            disabled={!canSave}
            onClick={handleSave}
          />
        </div>
      </Modal>

      {pendingRemoval ? (
        <RemoveScreenshotModal
          screenshot={pendingRemoval}
          isRemoving={isRemoving}
          errorMessage={removeError}
          onCancel={cancelRemoval}
          onConfirm={confirmRemoval}
        />
      ) : null}
    </>
  );
};

export default AddScreenshotsModal;
